import {
  InfiniteData,
  QueryClient,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { leadApi } from "api/lead";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { PaginatedResponse } from "shared/types";
import { Lead } from "views/leads-id/types";
import { Stage } from "views/settings-crm/types/stage";

const PIPELINE_COLUMN_KEY = "LEADS_PIPELINE_COLUMN";
const LEADS_TABLE_KEY = "LEADS_TABLE";
// The stage change is indexed by OpenSearch asynchronously, so an immediate
// refetch can still read the pre-move data. Reconcile on a short staggered
// schedule rather than a single fixed delay so a slow reindex still self-heals.
const REINDEX_RECONCILE_DELAYS_MS = [600, 1800];

type InfiniteLeads = InfiniteData<PaginatedResponse<Lead>>;
type ColumnCache = [readonly unknown[], InfiniteLeads | undefined];

interface ColumnSnapshot {
  queryKey: readonly unknown[];
  data: InfiniteLeads;
}

// Refetch the pipeline columns (so the moved card picks up its new
// recent_activities and most_recent_activity order) and the always-active list
// query. The optimistic move only rewrites current_stage, so without this the
// card would keep its pre-move recent_activities until an unrelated refetch.
const scheduleReconcile = (queryClient: QueryClient) =>
  REINDEX_RECONCILE_DELAYS_MS.forEach((delay) =>
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: [PIPELINE_COLUMN_KEY] });
      queryClient.invalidateQueries({ queryKey: [LEADS_TABLE_KEY] });
    }, delay)
  );

const findLead = (caches: ColumnCache[], leadId: number): Lead | undefined =>
  caches
    .flatMap(([, data]) => data?.pages ?? [])
    .flatMap((page) => page.items)
    .find((l) => l.id === leadId);

const removeLead = (data: InfiniteLeads, leadId: number): InfiniteLeads => {
  const hasLead = data.pages.some((page) =>
    page.items.some((l) => l.id === leadId)
  );
  if (!hasLead) return data;

  return {
    ...data,
    pages: data.pages.map((page, index) => ({
      ...page,
      items: page.items.filter((l) => l.id !== leadId),
      // The column badge reads pages[0].total_count, so always decrement the
      // first page regardless of which (load-more) page held the lead.
      total_count:
        index === 0 ? Math.max(0, page.total_count - 1) : page.total_count
    }))
  };
};

const prependLead = (data: InfiniteLeads, lead: Lead): InfiniteLeads => {
  const [firstPage, ...restPages] = data.pages;
  if (!firstPage) return data;
  return {
    ...data,
    pages: [
      {
        ...firstPage,
        items: [lead, ...firstPage.items],
        total_count: firstPage.total_count + 1
      },
      ...restPages
    ]
  };
};

export const useChangeLeadStage = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { leadId: number; stage: Stage },
    ColumnSnapshot[]
  >({
    mutationKey: ["LEAD_CHANGE_STAGE"],
    mutationFn: ({ leadId, stage }) =>
      leadApi.bulkChangeStage({ lead_ids: [leadId], stage_id: stage.id }),

    onMutate: async ({ leadId, stage }) => {
      // Cancel before reading so the snapshot/optimistic base isn't a
      // mid-flight refetch (avoids a TOCTOU between read and cancel).
      await queryClient.cancelQueries({ queryKey: [PIPELINE_COLUMN_KEY] });

      const caches = queryClient.getQueriesData<InfiniteLeads>({
        queryKey: [PIPELINE_COLUMN_KEY]
      });

      // Only move optimistically when the destination column is already loaded.
      // Otherwise removing the card from the source with no dest cache to place
      // it in would make it vanish; the unloaded path reconciles via a delayed
      // refetch in onSettled instead.
      const destLoaded = caches.some(
        ([queryKey, data]) => queryKey[1] === stage.id && Boolean(data)
      );
      if (!destLoaded) return [];

      const lead = findLead(caches, leadId);
      const movedLead = lead && {
        ...lead,
        current_stage_id: stage.id,
        current_stage: {
          id: stage.id,
          name: stage.name,
          color: stage.color,
          is_fixed: stage.is_fixed,
          position: String(stage.position)
        }
      };

      const snapshots: ColumnSnapshot[] = [];
      caches.forEach(([queryKey, data]) => {
        if (!data) return;
        snapshots.push({ queryKey, data });
        const withoutLead = removeLead(data, leadId);
        const isDestColumn = queryKey[1] === stage.id;
        if (isDestColumn && movedLead)
          queryClient.setQueryData<InfiniteLeads>(
            queryKey,
            prependLead(withoutLead, movedLead)
          );
        else queryClient.setQueryData<InfiniteLeads>(queryKey, withoutLead);
      });

      return snapshots;
    },
    onSuccess: () => {
      toast.success("Stage updated");
    },

    onError: (error, _vars, snapshots) => {
      snapshots?.forEach(({ queryKey, data }) =>
        queryClient.setQueryData<InfiniteLeads>(queryKey, data)
      );
      toast.error(getErrorMessage(error, "Failed to update lead stage"));
    },

    onSettled: (_data, error) => {
      // onError already rolled the caches back, so only reconcile on success.
      if (!error) scheduleReconcile(queryClient);
    }
  });
};
